import { Button } from '@ipfabric/ui-components/Button/Button';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import type { ReactNode } from 'react';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';
import { renderQuery } from 'utils/react-query/renderQuery';
import {
  type LabelEnrolment,
  useLabelEnrolmentQuery,
} from '../dataPoliciesApi';
import { EnrolledLabelsList } from './EnrolledLabelsList';
import { useEnrolmentDraft } from './useEnrolmentDraft';
import { useSaveEnrolment } from './useSaveEnrolment';

type EnrolmentPanelProps = {
  /** Closes the whole panel (header X). */
  onClose: () => void;
  /** Shows a back arrow, e.g. when opened as a sub-view of the policy form. */
  onBack?: () => void;
  /** Footer "Close" button. */
  onCancel: () => void;
  /** After a successful save. */
  onDone: () => void;
};

type FrameProps = Pick<EnrolmentPanelProps, 'onClose' | 'onBack'> & {
  footer: ReactNode;
  children: ReactNode;
};

const EnrolmentFrame = ({ onClose, onBack, footer, children }: FrameProps) => (
  <SidePanel
    title='Manage enrolled labels'
    onBack={onBack}
    closeOnBackdropClick={false}
    onClose={onClose}
    footer={footer}
  >
    {children}
  </SidePanel>
);

const CloseButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    className='flex-1'
    dataTestId='enrolled-labels-close'
    onClick={onClick}
    type='button'
    variant='outline'
  >
    Close
  </Button>
);

const EnrolmentEditor = ({
  entries,
  onClose,
  onBack,
  onCancel,
  onDone,
}: EnrolmentPanelProps & { entries: LabelEnrolment[] }) => {
  const { draft, toggle, isDirty, enrol, unenrol } = useEnrolmentDraft(entries);
  const { save, saving } = useSaveEnrolment(onDone);
  const canSave = useApiScopeRequirementMet()({
    method: 'PUT',
    path: '/labels/rbac-enrolment',
  });

  return (
    <EnrolmentFrame
      onClose={onClose}
      onBack={onBack}
      footer={
        <div className='flex gap-xs'>
          {canSave && (
            <Button
              className='flex-1'
              dataTestId='enrolled-labels-save'
              disabled={!isDirty}
              icon='fa-floppy-disk'
              loading={saving}
              onClick={() => void save({ enrol, unenrol })}
              type='button'
              variant='primary'
            >
              Save
            </Button>
          )}
          <CloseButton onClick={onCancel} />
        </div>
      }
    >
      <EnrolledLabelsList
        entries={entries}
        draft={draft}
        onToggle={toggle}
        readOnly={!canSave}
      />
    </EnrolmentFrame>
  );
};

/**
 * Loads enrolment and keeps the panel frame (with its close/back controls) in
 * every state, so a slow, failed or snapshot-less load never strands the user.
 */
export const EnrolmentPanel = (props: EnrolmentPanelProps) => {
  const snapshotId = useSnapshotId();
  const enrolmentQuery = useLabelEnrolmentQuery(snapshotId);
  const frame = (body: ReactNode) => (
    <EnrolmentFrame
      onClose={props.onClose}
      onBack={props.onBack}
      footer={<CloseButton onClick={props.onCancel} />}
    >
      {body}
    </EnrolmentFrame>
  );

  if (snapshotId === null) {
    return frame(
      <span className='text-element-secondary'>
        Select a snapshot to manage enrolled labels.
      </span>,
    );
  }

  return renderQuery({
    query: enrolmentQuery,
    renderStaleDataOnMount: true,
    pending: () => frame(<LoaderAndText />),
    error: () =>
      frame(
        <LoaderAndText hasError errorText='Failed to load enrolled labels' />,
      ),
    success: (entries) => <EnrolmentEditor {...props} entries={entries} />,
  });
};

type ManageEnrolledLabelsPanelProps = {
  onClose: () => void;
};

export const ManageEnrolledLabelsPanel = ({
  onClose,
}: ManageEnrolledLabelsPanelProps) => (
  <EnrolmentPanel onClose={onClose} onCancel={onClose} onDone={onClose} />
);
